export const TIME_FORMAT_PREFERENCES = ["auto", "12h", "24h"] as const
export type TimeFormatPreference = (typeof TIME_FORMAT_PREFERENCES)[number]

export const TIME_FORMAT_STORAGE_KEY = "timeFormat:v1"
export const TIME_FORMAT_COOKIE_NAME = "aient_time_format"

export function isTimeFormatPreference(value: unknown): value is TimeFormatPreference {
  return value === "auto" || value === "12h" || value === "24h"
}

export function parseTimeFormatPreference(value: unknown): TimeFormatPreference {
  return isTimeFormatPreference(value) ? value : "auto"
}

export function readTimeFormatCookie(cookieHeader: string | undefined | null): TimeFormatPreference {
  if (!cookieHeader) return "auto"
  const prefix = `${TIME_FORMAT_COOKIE_NAME}=`
  const part = cookieHeader.split(/;\s*/).find((row) => row.startsWith(prefix))
  if (!part) return "auto"
  try {
    return parseTimeFormatPreference(decodeURIComponent(part.slice(prefix.length)))
  } catch (error) {
    void error
    return "auto"
  }
}

export function detectHour12(locale?: string): boolean {
  const resolved = new Intl.DateTimeFormat(locale, { hour: "numeric" }).resolvedOptions()
  if (resolved.hourCycle === "h11" || resolved.hourCycle === "h12") return true
  if (resolved.hourCycle === "h23" || resolved.hourCycle === "h24") return false
  return resolved.hour12 === true
}

export function resolveHour12(
  preference: TimeFormatPreference,
  locale?: string,
): boolean {
  if (preference === "12h") return true
  if (preference === "24h") return false
  return detectHour12(locale)
}

export function readStoredTimeFormat(): TimeFormatPreference {
  if (typeof window === "undefined") return "auto"
  try {
    const raw = window.localStorage.getItem(TIME_FORMAT_STORAGE_KEY)
    if (isTimeFormatPreference(raw)) return raw
  } catch (error) {
    void error
  }
  return readTimeFormatCookie(document.cookie)
}

export function persistTimeFormat(preference: TimeFormatPreference): void {
  if (typeof window === "undefined") return
  try {
    window.localStorage.setItem(TIME_FORMAT_STORAGE_KEY, preference)
  } catch (error) {
    void error
    // Private mode / quota — cookie still lets refresh restore the choice.
  }
  const secure = window.location.protocol === "https:" ? "; Secure" : ""
  document.cookie = `${TIME_FORMAT_COOKIE_NAME}=${encodeURIComponent(preference)}; Path=/; Max-Age=31536000; SameSite=Lax${secure}`
}

export function formatClock(date: Date, options: {
  hour12: boolean
  locale?: string
  timeZone?: string
  bucketMs?: number
}): string {
  const bucketMs = options.bucketMs ?? 60_000
  return new Intl.DateTimeFormat(options.locale, {
    hour: options.hour12 ? "numeric" : "2-digit",
    minute: "2-digit",
    second: bucketMs < 60_000 ? "2-digit" : undefined,
    fractionalSecondDigits: bucketMs < 1_000 ? 3 : undefined,
    hour12: options.hour12,
    timeZone: options.timeZone,
  }).format(date)
}

/** UTC clock plus a `UTC` suffix — sparklines and other labelled UTC axes. */
export function formatUtcClockLabel(date: Date, options: {
  hour12: boolean
  locale?: string
  bucketMs?: number
}): string {
  return `${formatClock(date, { ...options, timeZone: "UTC" })} UTC`
}

function calendarDayKey(date: Date, options: { locale?: string; timeZone?: string }): string {
  return new Intl.DateTimeFormat("en-CA", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    timeZone: options.timeZone,
  }).format(date)
}

export function formatLocalStamp(date: Date, options: {
  hour12: boolean
  locale?: string
  timeZone?: string
  includeYear?: boolean
  bucketMs?: number
}): string {
  const datePart = new Intl.DateTimeFormat(options.locale, {
    month: "short",
    day: "numeric",
    year: options.includeYear ? "numeric" : undefined,
    timeZone: options.timeZone,
  }).format(date)
  return `${datePart}, ${formatClock(date, options)}`
}

export function formatOffsetStamp(date: Date, options: {
  hour12: boolean
  locale?: string
  timeZone?: string
  includeYear?: boolean
  bucketMs?: number
}): string {
  const stamp = formatLocalStamp(date, options)
  const offset = new Intl.DateTimeFormat(options.locale, {
    timeZone: options.timeZone,
    timeZoneName: "shortOffset",
  }).formatToParts(date).find((part) => part.type === "timeZoneName")?.value
    ?.replace(/^GMT/, "UTC")
  return offset ? `${stamp} ${offset}` : stamp
}

/** First/last observation stamps. Same local day omits the repeated date on last. */
export function formatSeenBounds(first: Date, last: Date, options: {
  hour12: boolean
  locale?: string
  timeZone?: string
  now?: Date
}): { first: string; last: string; sameDay: boolean } {
  const now = options.now ?? new Date()
  const includeYear =
    first.getFullYear() !== last.getFullYear()
    || first.getFullYear() !== now.getFullYear()
    || last.getFullYear() !== now.getFullYear()
  const sameDay = calendarDayKey(first, options) === calendarDayKey(last, options)
  return {
    first: formatLocalStamp(first, { ...options, includeYear }),
    last: sameDay
      ? formatClock(last, options)
      : formatLocalStamp(last, { ...options, includeYear }),
    sameDay,
  }
}
